/**
 * Clientes.tsx — Pantalla "Mis Clientes"
 *
 * Solo visible para usuarios con rol de administrador o vendedor.
 * Muestra la lista completa de clientes del sistema con:
 *   - Búsqueda por nombre, cédula, móvil o ID
 *   - Filtros por estado: Todos / Activos / Suspendidos / Retirados
 *   - Paginación: 25 / 50 / 100 / Todos por página
 *   - Al tocar un cliente → pantalla de detalle con servicios expandibles
 *
 * APIs que usa:
 *   - POST /api/v1/GetAllClients   → obtiene lista completa (máx 2000)
 *   - POST /api/v1/GetClientsDetails → obtiene detalle de un cliente específico
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import AppNavbar from '../components/AppNavbar';
import BottomNav from '../components/BottomNav';
import { Badge, Spinner, Form, InputGroup, Modal, Button } from 'react-bootstrap';
import { Browser } from '@capacitor/browser';
import { App } from '@capacitor/app';
import ComprobanteTicket from '../components/ComprobanteTicket';
import { guardarDetalleAbierto, leerDetalleAbierto, olvidarDetalleAbierto } from '../utils/detalleClienteAbierto';
import {
  FaSearch, FaArrowLeft, FaPhone, FaMobileAlt, FaEnvelope,
  FaMapMarkerAlt, FaWifi, FaIdCard, FaChevronDown, FaChevronUp,
  FaNetworkWired, FaCalendarAlt, FaDollarSign, FaUser, FaKey, FaPlug
} from 'react-icons/fa';
import './Clientes.css';

// Leemos la URL base de la API desde el archivo .env
// Ejemplo: si REACT_APP_API_URL = "https://sistema.sisnetel.com/api/v1/GetClient"
// entonces API_BASE = "https://sistema.sisnetel.com"
const N8N_CLIENTE_CEDULA   = process.env.REACT_APP_N8N_URL_CLIENTE_CEDULA!;
const N8N_CLIENTES_TODOS   = process.env.REACT_APP_N8N_URL_CLIENTES_TODOS!;
const N8N_DETALLE_FACTURA  = process.env.REACT_APP_N8N_URL_DETALLE_FACTURA!;
const N8N_COMPROBANTE      = process.env.REACT_APP_N8N_URL_COMPROBANTE!;

// ── Interfaces (definición de la forma de los datos) ─────────────────────────
// TypeScript usa interfaces para saber qué campos tiene cada objeto

/** Un servicio de internet/cable que tiene el cliente */
interface Servicio {
  id: number;
  perfil: string;         // Nombre del plan (ej: "50MB Fibra")
  tiposervicio: string;   // Tipo (ej: "Internet", "CATV")
  status_user: string;    // "ONLINE" o "OFFLINE"
  ip: string;             // Dirección IP asignada
  mac: string;            // Dirección MAC del router
  instalado: string;      // Fecha de instalación
  direccion: string;      // Dirección donde está instalado
  costo: string;          // Costo mensual del servicio
  pppuser: string;        // Usuario PPPoE del router
  ppppass: string;        // Contraseña PPPoE
  nodo: number;           // ID del nodo al que pertenece
  personalizados: string | Record<string, string>; // Campos extras como SSID y clave WiFi
  smartolt_catv: number;
}

/** Datos completos de un cliente (viene de GetClientsDetails) */
interface ClienteDetalle {
  id: number;
  nombre: string;
  estado: string;
  correo: string;
  telefono: string;
  movil: string;
  cedula: string;
  direccion_principal: string;
  ubicacion?: string;
  mantenimiento?: boolean;
  fecha_suspendido?: string;  // Fecha de corte (activos) o fecha de suspensión (suspendidos)
  servicios: Servicio[];
  facturacion?: { facturas_nopagadas: number; total_facturas: string };
  Promesa_Pago?: string;
  Modelo_Routers?: string;
  Id_Externo_Onu?: string;
}

/** Datos resumidos de un cliente (viene de GetAllClients — menos campos que el detalle) */
interface ClienteLista {
  id: number;
  nombre: string;
  estado: string;
  correo: string;
  telefono: string;
  movil: string;
  cedula: string;
  direccion_principal: string;
  servicios: number; // Solo el conteo de servicios, no el detalle
}

// Opciones de paginación. El 0 significa "mostrar todos"
const PAGE_SIZES = [25, 50, 100, 0];

// ── Funciones auxiliares ─────────────────────────────────────────────────────

/** Devuelve un Badge (etiqueta de color) según el estado del cliente */
const estadoBadge = (estado: string) => {
  const e = estado?.toUpperCase();
  if (e === 'ACTIVO')     return <Badge bg="success" style={{ fontSize: 11 }}>{estado}</Badge>;
  if (e === 'SUSPENDIDO') return <Badge bg="warning" text="dark" style={{ fontSize: 11 }}>{estado}</Badge>;
  if (e === 'RETIRADO')   return <Badge bg="danger" style={{ fontSize: 11 }}>{estado}</Badge>;
  return <Badge bg="secondary" style={{ fontSize: 11 }}>{estado}</Badge>;
};

/** Color del borde izquierdo de la tarjeta del cliente según su estado */
const borderColor = (estado: string) => {
  const e = estado?.toUpperCase();
  if (e === 'ACTIVO')     return 'var(--sn-exito)'; // verde
  if (e === 'SUSPENDIDO') return 'var(--sn-alerta)'; // amarillo
  return 'var(--sn-peligro)';                          // rojo
};

/** Color de fondo y texto del avatar (círculo con inicial del nombre) */
const avatarBg = (estado: string) => {
  const e = estado?.toUpperCase();
  if (e === 'ACTIVO')     return { bg: 'var(--sn-exito-suave)', color: 'var(--sn-exito-texto)' };
  if (e === 'SUSPENDIDO') return { bg: 'var(--sn-alerta-suave)', color: 'var(--sn-alerta-texto)' };
  return { bg: 'var(--sn-peligro-suave)', color: 'var(--sn-peligro-texto)' };
};

/** Opciones del filtro de estado */
const FILTROS = [
  { label: 'Todos',       value: 'TODOS' },
  { label: 'Activos',     value: 'ACTIVO' },
  { label: 'Suspendidos', value: 'SUSPENDIDO' },
  { label: 'Retirados',   value: 'RETIRADO' },
];

// ── Componente: fila de información (ícono + etiqueta + valor) ────────────────
const InfoRow: React.FC<{ icon: React.ReactNode; label: string; value: string }> = ({ icon, label, value }) => (
  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, paddingBottom: 8, marginBottom: 8, borderBottom: '1px solid var(--sn-borde)' }}>
    <span style={{ color: 'var(--sn-texto-3)', marginTop: 2, flexShrink: 0, fontSize: 13 }}>{icon}</span>
    <div>
      <div style={{ fontSize: 10, color: 'var(--sn-texto-3)', textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: 13, color: 'var(--sn-texto)', fontWeight: 500 }}>{value || '—'}</div>
    </div>
  </div>
);

// Versión compacta para el cuadro de info personal
const InfoRowCompact: React.FC<{ icon: React.ReactNode; label: string; value: string; copiable?: boolean }> = ({ icon, label, value, copiable }) => {
  const [copiado, setCopiado] = useState(false);
  const copiar = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!value) return;
    navigator.clipboard?.writeText(value).then(() => {
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    });
  };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 2 }}>
      <span style={{ color: 'var(--sn-texto-3)', flexShrink: 0, fontSize: 9 }}>{icon}</span>
      <span style={{ fontSize: 9, color: 'var(--sn-texto-3)', minWidth: 52 }}>{label}</span>
      <span style={{ fontSize: 10, color: 'var(--sn-texto)', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value || '—'}</span>
      {copiable && value && (
        <button
          type="button"
          onClick={copiar}
          style={{
            flexShrink: 0, fontSize: 8, fontWeight: 700, color: 'var(--sn-marca)', background: 'var(--sn-marca-suave)',
            border: '1px solid var(--sn-marca-suave)', borderRadius: 999, padding: '1px 5px', cursor: 'pointer',
          }}
        >
          {copiado ? '✓' : '📋'}
        </button>
      )}
    </div>
  );
};

// Parsea el string PHP serializado que devuelve SmartOLT (ej: a:17:{s:6:"olt_id";s:1:"9";...})
const parsePhpSerialized = (raw: string): Record<string, string> => {
  const result: Record<string, string> = {};
  if (!raw || typeof raw !== 'string') return result;
  const re = /s:\d+:"([^"]*)"/g;
  let m: RegExpExecArray | null;
  let lastKey = '';
  let idx = 0;
  while ((m = re.exec(raw)) !== null) {
    if (idx % 2 === 0) lastKey = m[1];
    else if (lastKey) { result[lastKey] = m[1]; lastKey = ''; }
    idx++;
  }
  return result;
};

// Decodifica un valor doble-URL-encoded (ej. OID de GenieACS)
const decodeDoble = (raw?: string): string => {
  if (!raw) return '';
  let value = raw;
  for (let i = 0; i < 2; i++) {
    try {
      const decoded = decodeURIComponent(value);
      if (decoded === value) break;
      value = decoded;
    } catch { break; }
  }
  return value;
};

// Formatea "2026-07-01 17:38:43" → "01/07 17:38"
const formatFecha = (raw?: string): string | undefined => {
  if (!raw) return undefined;
  const m = raw.match(/^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2})/);
  if (!m) return raw;
  return `${m[3]}/${m[2]} ${m[4]}:${m[5]}`;
};

// Formatea bytes a KB/MB/GB legibles
const formatBytes = (raw?: number): string | undefined => {
  if (raw === undefined || raw === null) return undefined;
  if (raw < 1024) return `${raw} B`;
  if (raw < 1024 ** 2) return `${(raw / 1024).toFixed(1)} KB`;
  if (raw < 1024 ** 3) return `${(raw / 1024 ** 2).toFixed(1)} MB`;
  return `${(raw / 1024 ** 3).toFixed(2)} GB`;
};

// Limpia el nombre del plan de velocidad, ej. "plan_700_Mbps" → "700 Mbps"
const formatPlan = (raw?: string): string | undefined => {
  if (!raw) return undefined;
  return raw.replace(/^plan_/, '').replace(/_/g, ' ');
};

// ── Componente: tarjeta de un servicio (expandible) ───────────────────────────
const ServicioCard: React.FC<{ srv: Servicio; idx: number; onChangeTab: (tab: TabId) => void }> = ({ srv, idx, onChangeTab }) => {
  const [open, setOpen] = useState(false);
  const [activeSection, setActiveSection] = useState<number>(-1);
  const [showMikrotikModal, setShowMikrotikModal] = useState(false);

  const parsePersonalizados = () => {
    try {
      if (typeof srv.personalizados === 'string') return JSON.parse(srv.personalizados);
      return srv.personalizados || {};
    } catch { return {}; }
  };

  const personaliz    = parsePersonalizados();
  const usuarioEquipo = personaliz['1746761039'] || '';
  const claveEquipo   = personaliz['1746761040'] || '';
  const puertoRouter  = personaliz['1744779595'] || '';
  const modeloRouter  = personaliz['1746760491'] || '';
  const oidGenieAcs   = personaliz['1770612677'] || '';
  const smartolt      = parsePhpSerialized((srv as any).smartolt || '');
  const isOnline      = srv.status_user?.toUpperCase() === 'ONLINE';
  // Solo los routers Cudy sirven su panel por HTTPS; el resto (Huawei, TP-Link…)
  // van por HTTP. El modelo es texto libre, así que se busca "cudy" en cualquier
  // parte y sin distinguir mayúsculas ("Cudy", "CUDY", "Cudy WR3000H"…).
  // Si el modelo viene vacío se asume HTTP, que es la regla general.
  const esCudy        = /cudy/i.test(modeloRouter);
  const protocolo     = esCudy ? 'https' : 'http';
  const ipPuerto      = srv.ip && puertoRouter ? `${protocolo}://${srv.ip}:${puertoRouter}` : (srv.ip ? `${protocolo}://${srv.ip}` : '');

  const Cell: React.FC<{ label: string; value: string; full?: boolean; link?: boolean }> = ({ label, value, full, link }) => (
    <div style={{ gridColumn: full ? '1/-1' : undefined, background: 'var(--sn-superficie-2)', border: '0.5px solid var(--sn-borde)', borderRadius: 6, padding: '5px 7px' }}>
      <div style={{ fontSize: 8, color: 'var(--sn-texto-3)', textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 1 }}>{label}</div>
      {link && value ? (
        <div
          onClick={() => window.open(value, '_system')}
          style={{ fontSize: 10, fontWeight: 600, color: 'var(--sn-accion-hover)', textDecoration: 'underline', cursor: 'pointer', wordBreak: 'break-all' }}
        >{value}</div>
      ) : (
        <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--sn-texto)', wordBreak: 'break-word' }}>{value || '—'}</div>
      )}
    </div>
  );

  const Grid: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>{children}</div>
  );

  const btnStyle = (color: string): React.CSSProperties => ({
    width: '100%', marginTop: 8, padding: '8px 12px', border: 'none', borderRadius: 8,
    fontSize: 11, fontWeight: 700, cursor: 'pointer', color: 'var(--sn-accion-texto)', background: color,
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
  });

  const sections = [
    {
      icon: '🌐', label: 'Red',
      content: (
        <>
          <Grid>
            <Cell label="IP"   value={srv.ip} />
            <Cell label="MAC"  value={srv.mac} />
            <Cell label="Nodo" value={String(srv.nodo || '')} />
            {smartolt.vlan ? <Cell label="VLAN" value={smartolt.vlan} /> : <div />}
          </Grid>
          <button style={btnStyle('linear-gradient(135deg,var(--sn-accion-hover),var(--sn-accion))')} onClick={() => setShowMikrotikModal(true)}>
            🛡️ Comprobar Mikrotik
          </button>
        </>
      ),
    },
    {
      icon: '📡', label: 'ONU',
      content: (
        <>
          <Grid>
            {smartolt.onu_type ? <Cell label="Tipo ONU"  value={smartolt.onu_type} /> : null}
            {smartolt.sn       ? <Cell label="Serial SN" value={smartolt.sn} /> : null}
            {smartolt.pon_type ? <Cell label="PON"       value={smartolt.pon_type.toUpperCase()} /> : null}
            {smartolt.onu_mode ? <Cell label="Modo"      value={smartolt.onu_mode} /> : null}
            {smartolt.olt_id   ? <Cell label="OLT ID"    value={smartolt.olt_id} /> : null}
            {(smartolt.board && smartolt.port) ? <Cell label="Board/Port" value={`${smartolt.board} / ${smartolt.port}`} /> : null}
            {smartolt.zone ? <Cell label="Zona" value={smartolt.zone} full /> : null}
            {smartolt.upload_speed_profile_name ? (
              <Cell label="Perfil velocidad"
                value={smartolt.upload_speed_profile_name === smartolt.download_speed_profile_name
                  ? `${smartolt.upload_speed_profile_name} ↑↓`
                  : `↑ ${smartolt.upload_speed_profile_name}  ↓ ${smartolt.download_speed_profile_name}`}
                full />
            ) : null}
          </Grid>
          <button style={btnStyle('linear-gradient(135deg,var(--sn-accion-hover),var(--sn-accion))')} onClick={() => onChangeTab('smartolt')}>
            📡 Comprobar SmartOLT
          </button>
        </>
      ),
    },
    {
      icon: '🔧', label: 'Equipo',
      content: (
        <>
          <Grid>
            {modeloRouter  ? <Cell label="Modelo router"    value={modeloRouter} full /> : null}
            {ipPuerto      ? <Cell label="IP / Puerto"      value={ipPuerto} full link /> : null}
            {usuarioEquipo ? <Cell label="Usuario equipo"   value={usuarioEquipo} /> : null}
            {claveEquipo   ? <Cell label="Contraseña equipo" value={claveEquipo} full /> : null}
            {oidGenieAcs   ? <Cell label="OID GenieACS"     value={decodeDoble(oidGenieAcs)} full /> : null}
          </Grid>
          <button style={btnStyle('linear-gradient(135deg,var(--sn-accion-hover),var(--sn-accion))')} onClick={() => onChangeTab('tr069')}>
            🔧 Comprobar TR-069
          </button>
        </>
      ),
    },
    {
      icon: '📋', label: 'Instalación',
      content: (
        <Grid>
          <Cell label="Instalado"    value={srv.instalado} />
          <Cell label="Costo"        value={`$${srv.costo}`} />
          <Cell label="Dirección"    value={srv.direccion} full />
        </Grid>
      ),
    },
  ];

  const toggleSection = (i: number) => setActiveSection(prev => prev === i ? -1 : i);

  return (
    <>
      <div style={{ border: '1px solid var(--sn-borde)', borderRadius: 10, marginBottom: 8, overflow: 'hidden' }}>
        {/* Header */}
        <div onClick={() => { setOpen(o => !o); setActiveSection(-1); }}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', cursor: 'pointer', background: open ? 'var(--sn-superficie-2)' : 'var(--sn-superficie)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: isOnline ? 'var(--sn-exito)' : 'var(--sn-texto-3)', flexShrink: 0 }} />
            <div>
              <div style={{ fontWeight: 600, fontSize: 13 }}>
                Servicio #{idx + 1}
                <span style={{ marginLeft: 6, fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 20, background: isOnline ? 'var(--sn-exito-suave)' : 'var(--sn-peligro-suave)', color: isOnline ? 'var(--sn-exito)' : 'var(--sn-peligro-texto)' }}>
                  {isOnline ? 'ONLINE' : 'OFFLINE'}
                </span>
              </div>
              <div style={{ fontSize: 11, color: 'var(--sn-texto-3)' }}>{srv.perfil} · ${srv.costo}/mes</div>
            </div>
          </div>
          <span style={{ color: 'var(--sn-texto-3)' }}>{open ? <FaChevronUp size={13} /> : <FaChevronDown size={13} />}</span>
        </div>

        {open && (
          <div style={{ background: 'var(--sn-superficie-2)', borderTop: '1px solid var(--sn-borde)', padding: '10px 10px 8px' }}>
            {/* Chips 1x4 */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 5, marginBottom: 6 }}>
              {sections.map((s, i) => (
                <div key={i} onClick={() => toggleSection(i)}
                  style={{ border: '0.5px solid var(--sn-borde)', borderRadius: 8, overflow: 'hidden', cursor: 'pointer', background: 'var(--sn-superficie)' }}>
                  <div style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, padding: '6px 4px',
                    background: activeSection === i ? 'linear-gradient(135deg,var(--sn-accion-hover),var(--sn-accion))' : undefined,
                  }}>
                    <span style={{ fontSize: 16 }}>{s.icon}</span>
                    <span style={{ fontSize: 8, fontWeight: 700, color: activeSection === i ? 'var(--sn-accion-texto)' : 'var(--sn-texto-2)', textAlign: 'center', lineHeight: 1.2 }}>{s.label}</span>
                  </div>
                </div>
              ))}
            </div>
            {/* Panel de contenido */}
            {activeSection >= 0 && (
              <div style={{ background: 'var(--sn-superficie)', border: '0.5px solid var(--sn-borde)', borderRadius: 8, padding: 8 }}>
                {sections[activeSection].content}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal Mikrotik */}
      {showMikrotikModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'var(--sn-superficie)', borderRadius: 16, padding: '24px 20px', maxWidth: 280, width: '90%', textAlign: 'center' }}>
            <div style={{ fontSize: 40, marginBottom: 10 }}>🛠️</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--sn-accion-hover)', marginBottom: 6 }}>En desarrollo</div>
            <div style={{ fontSize: 12, color: 'var(--sn-texto-3)', lineHeight: 1.5, marginBottom: 16 }}>Estamos trabajando en esta opción. Pronto estará disponible.</div>
            <button onClick={() => setShowMikrotikModal(false)}
              style={{ width: '100%', padding: '9px 24px', background: 'linear-gradient(135deg,var(--sn-accion-hover),var(--sn-accion))', color: 'var(--sn-accion-texto)', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
              Entendido
            </button>
          </div>
        </div>
      )}
    </>
  );
};

const N8N_FACTURAS_CLIENTE  = process.env.REACT_APP_N8N_URL_FACTURAS_CLIENTE!;
const N8N_PAYPHONE_LINK     = process.env.REACT_APP_N8N_PAYPHONE_LINK!;

type TabId = 'servicios' | 'facturas' | 'smartolt' | 'tr069';

const TABS: { id: TabId; label: string; icon: string }[] = [
  { id: 'servicios', label: 'Servicios', icon: '📡' },
  { id: 'facturas',  label: 'Facturas',  icon: '🧾' },
  { id: 'smartolt',  label: 'SmartOLT',  icon: '🔦' },
  { id: 'tr069',     label: 'TR-069',    icon: '🔧' },
];

interface Factura {
  id: number;
  total: string;
  estado: string;
  emitido: string;
  vencimiento: string;
  saldo: string;
  impuesto: string;
  fechapago: string;
  subtotal: string;
}

type FiltroFactura = '' | '1' | '0';

// ── Modal detalle de factura ──────────────────────────────────────────────────
const ModalFactura: React.FC<{
  factura: Factura;
  cedula: string;
  nombre: string;
  onClose: () => void;
  onLinkListo: (link: string) => void;
}> = ({ factura, cedula, nombre, onClose, onLinkListo }) => {
  const navigate = useNavigate();
  const [detalle,         setDetalle]         = useState<any>(null);
  const [loadingDet,      setLoadingDet]      = useState(true);
  const [svgBase64,       setSvgBase64]       = useState('');
  const [loadingComp,     setLoadingComp]     = useState(false);
  const [errorComp,       setErrorComp]       = useState('');
  const [showTicket,      setShowTicket]      = useState(false);
  const [loadingLink,     setLoadingLink]     = useState(false);
  const [errorLink,       setErrorLink]       = useState('');
  const [payphoneLink,    setPayphoneLink]    = useState('');
  const [showConfirmPago, setShowConfirmPago] = useState(false);

  const generarLink = async () => {
    setLoadingLink(true);
    setErrorLink('');
    try {
      const montoInt = Math.round(parseFloat(factura.total || '0') * 100);
      const res = await fetch(N8N_PAYPHONE_LINK, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cedula, nombre, monto: montoInt, facturaId: factura.id }),
      });
      const data = await res.json();
      const raw = Array.isArray(data) ? data[0] : data;
      if (raw?.link) {
        setPayphoneLink(raw.link);
        setShowConfirmPago(true);
      } else {
        setErrorLink('No se pudo generar el link de pago.');
      }
    } catch {
      setErrorLink('Error al generar el link.');
    } finally {
      setLoadingLink(false);
    }
  };

  const pagada = factura.estado?.toLowerCase() === 'pagado';

  useEffect(() => {
    fetch(N8N_DETALLE_FACTURA, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ facturas_id: String(factura.id) }),
    })
      .then(r => r.json())
      .then(data => {
        const raw = Array.isArray(data) ? data[0] : data;
        if (raw?.estado === 'exito') setDetalle(raw);
      })
      .catch(() => {})
      .finally(() => setLoadingDet(false));
  }, [factura.id]);

  const verComprobante = async () => {
    if (svgBase64) { setShowTicket(true); return; }
    setLoadingComp(true);
    setErrorComp('');
    try {
      const fac   = detalle?.factura ?? {};
      const items: any[] = detalle?.items ?? [];
      const item  = items.find((i: any) => parseFloat(i.total) > 0) || items[0];
      const lines: string[] = (item?.descrp || '').split('\n').map((l: string) => l.trim()).filter(Boolean);

      const clientData = (() => { try { return JSON.parse(localStorage.getItem('clientData') || '{}'); } catch { return {}; } })();
      const now  = new Date();
      const p2   = (n: number) => String(n).padStart(2, '0');
      const fechaImpresion = `${p2(now.getDate())}/${p2(now.getMonth()+1)}/${now.getFullYear()} ${p2(now.getHours())}:${p2(now.getMinutes())}:${p2(now.getSeconds())}`;
      const fmtDate = (d: string) => { if (!d || d.startsWith('0000')) return ''; const pts = d.split('-'); return pts.length === 3 ? `${pts[2]}/${pts[1]}/${pts[0]}` : d; };

      const payload = {
        clienteNombre:     nombre,
        clienteCedula:     cedula,
        clienteCelular:    clientData?.movil   || '',
        clienteCorreo:     clientData?.correo  || '',
        facturaId:         String(fac.id || factura.id).padStart(8, '0'),
        planNombre:        lines[0] || '',
        facturacion:       (lines[2] || '').replace('facturación del ', ''),
        suspension:        (lines[3] || '').replace('Fecha de Suspensión : ', ''),
        fechaPago:         fmtDate(fac.fechapago || factura.fechapago),
        vencimiento:       fmtDate(fac.vencimiento || factura.vencimiento),
        subtotal:          parseFloat(fac.subtotal  || factura.subtotal  || '0').toFixed(2),
        iva:               parseFloat(fac.impuesto  || factura.impuesto  || '0').toFixed(2),
        total:             parseFloat(fac.total     || factura.total     || '0').toFixed(2),
        saldo:             parseFloat(fac.saldo     || factura.saldo     || '0').toFixed(2),
        pasarela:          'Depósito',
        numeroComprobante: '',
        autorizacion:      '',
        montoDepositado:   parseFloat(fac.total || factura.total || '0').toFixed(2),
        fechaImpresion,
      };

      const res     = await fetch(N8N_COMPROBANTE, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      const compData = await res.json();
      if (compData?.base64) { setSvgBase64(compData.base64); setShowTicket(true); }
      else setErrorComp('No se pudo generar el comprobante.');
    } catch { setErrorComp('Error al generar el comprobante.'); }
    finally  { setLoadingComp(false); }
  };

  return (
    <>
    {/* Modal de confirmación de pago */}
    {showConfirmPago && (
      <div style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9999,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
      }}>
        <div style={{ background: 'var(--sn-superficie)', borderRadius: 16, padding: 24, width: '100%', maxWidth: 340, boxShadow: 'var(--sn-sombra)' }}>
          <div style={{ textAlign: 'center', marginBottom: 20 }}>
            <div style={{ fontSize: 40, marginBottom: 8 }}>💳</div>
            <div style={{ fontWeight: 700, fontSize: 16, color: 'var(--sn-texto)' }}>Link de Pago Listo</div>
            <div style={{ fontSize: 12, color: 'var(--sn-texto-3)', marginTop: 4 }}>Confirma los datos antes de abrir</div>
          </div>
          <div style={{ background: 'var(--sn-superficie-2)', borderRadius: 12, padding: '14px 16px', marginBottom: 20, border: '1px solid var(--sn-borde)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
              <div>
                <div style={{ fontSize: 10, color: 'var(--sn-texto-3)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Cliente</div>
                <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--sn-texto)' }}>{nombre}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 10, color: 'var(--sn-texto-3)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Cédula</div>
                <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--sn-texto)' }}>{cedula}</div>
              </div>
            </div>
            <div style={{ borderTop: '1px solid var(--sn-borde)', paddingTop: 10 }}>
              <div style={{ fontSize: 10, color: 'var(--sn-texto-3)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 2 }}>Monto a cobrar</div>
              <div style={{ fontWeight: 700, fontSize: 28, color: 'var(--sn-alerta-texto)' }}>${parseFloat(factura.total || '0').toFixed(2)}</div>
              <div style={{ fontSize: 10, color: 'var(--sn-texto-3)' }}>Factura #{factura.id}</div>
            </div>
          </div>
          <button
            onClick={() => { setShowConfirmPago(false); onClose(); onLinkListo(payphoneLink); }}
            style={{
              width: '100%', padding: '14px 0', border: 'none', borderRadius: 12, marginBottom: 10,
              background: 'linear-gradient(135deg, var(--sn-accion-hover) 0%, var(--sn-accion) 100%)',
              color: 'var(--sn-accion-texto)', fontWeight: 700, fontSize: 15, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
              boxShadow: 'var(--sn-sombra)',
            }}
          >
            <span style={{ fontSize: 20 }}>🌐</span>
            Abrir ventana de pago
          </button>
          <button
            onClick={() => setShowConfirmPago(false)}
            style={{ width: '100%', padding: '10px 0', border: '1px solid var(--sn-borde)', borderRadius: 12, background: 'var(--sn-superficie)', color: 'var(--sn-texto-3)', fontWeight: 600, fontSize: 14, cursor: 'pointer' }}
          >
            Cancelar
          </button>
        </div>
      </div>
    )}

    <Modal show onHide={onClose} centered>
      <Modal.Body style={{ padding: 0 }}>

        {showTicket && svgBase64 ? (
          <div style={{ padding: '12px 16px' }}>
            <ComprobanteTicket svgBase64={svgBase64} facturaId={factura.id} onClose={() => setShowTicket(false)} />
          </div>
        ) : (
          <>
            {/* Header azul gradiente */}
            <div style={{
              background: 'linear-gradient(135deg, var(--sn-accion-hover) 0%, var(--sn-accion) 60%, var(--sn-accion) 100%)',
              borderRadius: '12px 12px 0 0',
              padding: '14px 16px',
              color: 'var(--sn-accion-texto)',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 700 }}>Factura #{factura.id}</div>
                  <div style={{
                    display: 'inline-block', marginTop: 4, padding: '2px 10px', borderRadius: 20, fontSize: '0.7rem', fontWeight: 700,
                    background: pagada ? 'var(--sn-exito)' : 'rgba(255,255,255,0.2)',
                    color: pagada ? 'var(--sn-texto-invertido)' : 'var(--sn-accion-texto)',
                  }}>
                    {pagada ? '✅ Pagada' : '⏳ No pagada'}
                  </div>
                </div>
                <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: 8, color: 'var(--sn-accion-texto)', width: 28, height: 28, cursor: 'pointer', fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6 }}>
                <div>
                  <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.65rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>Emitida</div>
                  <div style={{ fontSize: '0.75rem', fontWeight: 600 }}>{factura.emitido}</div>
                </div>
                <div>
                  <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.65rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>Vencimiento</div>
                  <div style={{ fontSize: '0.75rem', fontWeight: 600 }}>{factura.vencimiento}</div>
                </div>
                {pagada && factura.fechapago && !factura.fechapago.startsWith('0000') && (
                  <div>
                    <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.65rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>Fecha pago</div>
                    <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--sn-accion-texto)' }}>{factura.fechapago}</div>
                  </div>
                )}
              </div>
            </div>

            {/* Cuerpo */}
            <div style={{ padding: '12px 16px' }}>

              {/* Total grande centrado */}
              <div style={{ textAlign: 'center', padding: '10px 0 8px' }}>
                <div style={{ color: 'var(--sn-texto-2)', fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 2 }}>Total a pagar</div>
                <div style={{ fontSize: '2.4rem', fontWeight: 900, color: pagada ? 'var(--sn-exito)' : 'var(--sn-peligro)', lineHeight: 1 }}>
                  ${parseFloat(factura.total || '0').toFixed(2)}
                </div>
                <div style={{ color: 'var(--sn-texto-3)', fontSize: '0.7rem', marginTop: 1 }}>USD</div>
              </div>

              {/* Detalle items */}
              {loadingDet ? (
                <div style={{ textAlign: 'center', padding: 12 }}>
                  <Spinner animation="border" size="sm" variant="primary" />
                  <p style={{ fontSize: 12, color: 'var(--sn-texto-3)', marginTop: 6 }}>Cargando detalle...</p>
                </div>
              ) : detalle ? (
                <>
                  <div style={{ background: 'var(--sn-superficie-2)', borderRadius: 8, padding: '8px 12px', marginBottom: 8 }}>
                    {(detalle.items ?? []).map((item: any, i: number) => {
                      const lines = (item.descrp || '').split('\n').map((l: string) => l.trim()).filter(Boolean);
                      const fechaCorte = lines.find((l: string) => l.startsWith('Fecha de Suspensión'))?.replace('Fecha de Suspensión : ', '');
                      return (
                        <div key={i}>
                          <div style={{ fontSize: 12, color: 'var(--sn-texto-2)', marginBottom: fechaCorte && !pagada ? 6 : 0 }}>{lines[0]}</div>
                          {fechaCorte && !pagada && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                              <span style={{ fontSize: 13 }}>✂️</span>
                              <div>
                                <div style={{ fontSize: 9, color: 'var(--sn-texto-3)', textTransform: 'uppercase' }}>Fecha de corte</div>
                                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--sn-peligro)' }}>{fechaCorte}</div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {pagada && (detalle.factura?.formapago || detalle.factura?.pasarela) && (
                    <div style={{ background: 'var(--sn-info-suave)', borderRadius: 8, padding: '6px 12px', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 14 }}>💳</span>
                      <div>
                        <div style={{ fontSize: 9, color: 'var(--sn-texto-3)', textTransform: 'uppercase' }}>Método de pago</div>
                        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--sn-accion-hover)' }}>{detalle.factura?.formapago || detalle.factura?.pasarela || '—'}</div>
                      </div>
                    </div>
                  )}
                </>
              ) : null}

              {/* Subtotales */}
              <div style={{ borderTop: '1px solid var(--sn-borde)', paddingTop: 8, marginBottom: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--sn-texto-3)', marginBottom: 3 }}>
                  <span>Subtotal</span><span>${parseFloat(factura.subtotal || '0').toFixed(2)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--sn-texto-3)' }}>
                  <span>IVA</span><span>${parseFloat(factura.impuesto || '0').toFixed(2)}</span>
                </div>
              </div>

              {/* Botón pagar */}
              {!pagada && (
                <button
                  onClick={() => { onClose(); navigate(`/r/${btoa(cedula)}`); }}
                  style={{
                    width: '100%', padding: '12px 0', border: 'none', borderRadius: 12,
                    background: 'linear-gradient(135deg, var(--sn-accion-hover) 0%, var(--sn-accion) 100%)',
                    color: 'var(--sn-accion-texto)', fontWeight: 700, fontSize: 15, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                    boxShadow: 'var(--sn-sombra)',
                  }}
                >
                  <span style={{ background: 'rgba(255,255,255,0.2)', borderRadius: 8, width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 17 }}>🏦</span>
                  Pagar Ahora
                </button>
              )}

              {/* Botón comprobante */}
              {pagada && (
                <div>
                  {errorComp && <p style={{ fontSize: 11, color: 'var(--sn-peligro)', marginBottom: 6 }}>{errorComp}</p>}
                  <Button variant="primary" className="w-100" onClick={verComprobante} disabled={loadingComp}>
                    {loadingComp
                      ? <><Spinner animation="border" size="sm" className="me-2" />Generando...</>
                      : '🧾 Ver comprobante'}
                  </Button>
                </div>
              )}

              <button onClick={onClose} style={{ width: '100%', marginTop: 10, background: 'none', border: '0.5px solid var(--sn-borde)', borderRadius: 10, padding: '8px 0', fontSize: 12, color: 'var(--sn-texto-3)', cursor: 'pointer' }}>
                Cerrar
              </button>
            </div>
          </>
        )}
      </Modal.Body>
    </Modal>
    </>
  );
};

// ── Pestaña Facturas ──────────────────────────────────────────────────────────
export const TabFacturas: React.FC<{ clienteId: number; cedula: string; nombre: string }> = ({ clienteId, cedula, nombre }) => {
  const [facturas,          setFacturas]          = useState<Factura[]>([]);
  const [loading,           setLoading]           = useState(true);
  const [error,             setError]             = useState('');
  const [filtro,            setFiltro]            = useState<FiltroFactura>('');
  const [facturaSeleccionada, setFacturaSeleccionada] = useState<Factura | null>(null);

  const fetchFacturas = (estado: string): Promise<Factura[]> =>
    fetch(N8N_FACTURAS_CLIENTE, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ idcliente: clienteId, estado, limit: 50 }),
    })
      .then(r => r.json())
      .then(data => {
        const raw = Array.isArray(data) ? data[0] : data;
        return (raw?.facturas ?? []) as Factura[];
      });

  const ordenar = (lista: Factura[]) => {
    lista.sort((a, b) => {
      const aPagada = a.estado?.toLowerCase() === 'pagado' ? 1 : 0;
      const bPagada = b.estado?.toLowerCase() === 'pagado' ? 1 : 0;
      if (aPagada !== bPagada) return aPagada - bPagada;
      return new Date(b.emitido).getTime() - new Date(a.emitido).getTime();
    });
    return lista;
  };

  const cargar = (estado: FiltroFactura) => {
    setLoading(true);
    setError('');
    const peticion = estado === ''
      ? Promise.all([fetchFacturas('1'), fetchFacturas('0')]).then(([np, p]) => [...np, ...p])
      : fetchFacturas(estado);

    peticion
      .then(lista => setFacturas(ordenar(lista)))
      .catch(() => setError('Error al cargar facturas.'))
      .finally(() => setLoading(false));
  };

  const handleLinkListo = async (link: string) => {
    const listener = await App.addListener('appStateChange', ({ isActive }) => {
      if (isActive) {
        listener.remove();
        cargar(filtro);
      }
    });

    await Browser.open({ url: link, presentationStyle: 'popover' });
  };

  useEffect(() => { cargar(filtro); }, [clienteId, filtro]);

  const noPagadas = facturas.filter(f => f.estado?.toLowerCase() !== 'pagado');
  const totalDeuda = noPagadas.reduce((acc, f) => acc + parseFloat(f.total || '0'), 0);

  return (
    <div style={{ padding: '12px 14px' }}>

      {/* Filtros */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
        {([['', 'Todas'], ['1', 'No pagadas'], ['0', 'Pagadas']] as [FiltroFactura, string][]).map(([val, label]) => (
          <button key={val} onClick={() => setFiltro(val)} style={{
            border: 'none', borderRadius: 20, padding: '4px 14px', fontSize: 12, fontWeight: 600,
            cursor: 'pointer',
            background: filtro === val ? 'var(--sn-accion)' : 'var(--sn-superficie-3)',
            color: filtro === val ? 'var(--sn-accion-texto)' : 'var(--sn-texto-2)',
          }}>{label}</button>
        ))}
      </div>

      {loading && (
        <div style={{ textAlign: 'center', padding: 32 }}>
          <Spinner animation="border" variant="primary" size="sm" />
          <p className="mt-2 text-muted" style={{ fontSize: 13 }}>Cargando facturas...</p>
        </div>
      )}

      {error && <p style={{ color: 'var(--sn-peligro)', textAlign: 'center', fontSize: 13 }}>{error}</p>}

      {!loading && !error && (
        <>
          {/* Resumen deuda (solo cuando hay no pagadas visibles) */}
          {noPagadas.length > 0 && filtro !== '0' && (
            <div style={{
              background: 'var(--sn-alerta-suave)', border: '1px solid var(--sn-alerta-suave)', borderRadius: 10,
              padding: '10px 14px', marginBottom: 12,
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}>
              <div>
                <div style={{ fontSize: 10, color: 'var(--sn-texto-3)', textTransform: 'uppercase', letterSpacing: 0.8 }}>No pagadas</div>
                <div style={{ fontWeight: 700, fontSize: 20, color: 'var(--sn-alerta-texto)' }}>{noPagadas.length}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 10, color: 'var(--sn-texto-3)', textTransform: 'uppercase', letterSpacing: 0.8 }}>Total deuda</div>
                <div style={{ fontWeight: 700, fontSize: 20, color: 'var(--sn-alerta-texto)' }}>${totalDeuda.toFixed(2)}</div>
              </div>
            </div>
          )}

          {facturas.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 32, color: 'var(--sn-texto-3)' }}>
              <div style={{ fontSize: 36 }}>🧾</div>
              <p className="mt-2" style={{ fontSize: 13 }}>Sin facturas en esta categoría.</p>
            </div>
          ) : facturas.map(f => {
            const pagada = f.estado?.toLowerCase() === 'pagado';
            return (
              <div key={f.id} onClick={() => setFacturaSeleccionada(f)} style={{
                background: 'var(--sn-superficie)', borderRadius: 10, marginBottom: 8,
                border: `1px solid ${pagada ? 'var(--sn-exito-suave)' : 'var(--sn-alerta-suave)'}`,
                borderLeft: `4px solid ${pagada ? 'var(--sn-exito)' : 'var(--sn-alerta)'}`,
                padding: '10px 12px', cursor: 'pointer',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 13 }}>Factura #{f.id}</div>
                    <div style={{ fontSize: 11, color: 'var(--sn-texto-3)', marginTop: 2 }}>Emitida: {f.emitido ?? '—'}</div>
                    <div style={{ fontSize: 11, color: 'var(--sn-texto-3)' }}>Vence: {f.vencimiento ?? '—'}</div>
                    {f.fechapago && !f.fechapago.startsWith('0000') && (
                      <div style={{ fontSize: 11, color: 'var(--sn-exito)' }}>Pagada: {f.fechapago}</div>
                    )}
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontWeight: 700, fontSize: 16, color: pagada ? 'var(--sn-exito)' : 'var(--sn-alerta-texto)' }}>
                      ${parseFloat(f.total || '0').toFixed(2)}
                    </div>
                    <span style={{
                      display: 'inline-block', marginTop: 4,
                      fontSize: 10, fontWeight: 700, borderRadius: 10, padding: '2px 8px',
                      background: pagada ? 'var(--sn-exito-suave)' : 'var(--sn-alerta-suave)',
                      color: pagada ? 'var(--sn-exito-texto)' : 'var(--sn-alerta-texto)',
                    }}>
                      {pagada ? '✅ Pagada' : '⏳ No pagada'}
                    </span>
                    {!pagada && (
                      <div style={{ fontSize: 10, color: 'var(--sn-texto-3)', marginTop: 2 }}>
                        IVA: ${parseFloat(f.impuesto || '0').toFixed(2)}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}

          {facturaSeleccionada && (
            <ModalFactura
              factura={facturaSeleccionada}
              cedula={cedula}
              nombre={nombre}
              onClose={() => setFacturaSeleccionada(null)}
              onLinkListo={handleLinkListo}
            />
          )}
        </>
      )}
    </div>
  );
};

// ── Pestaña SmartOLT: detalle completo de cada ONT del cliente ───────────────
const N8N_ONU_SIGNAL = process.env.REACT_APP_N8N_URL_ONU_SIGNAL!;

// Convierte "2026-07-01 17:38:43" en algo tipo "hace 4 días"
const tiempoDesde = (raw?: string): string => {
  if (!raw || raw.startsWith('0000-00-00')) return '';
  const diffMs = Date.now() - new Date(raw.replace(' ', 'T')).getTime();
  const dias = Math.floor(diffMs / 86400000);
  if (dias <= 0) return 'hoy';
  if (dias === 1) return 'hace 1 día';
  return `hace ${dias} días`;
};

const OltCell: React.FC<{ label: string; value?: string | number | null; full?: boolean }> = ({ label, value, full }) => (
  !value && value !== 0 ? null : (
    <div style={{ gridColumn: full ? '1/-1' : undefined, background: 'var(--sn-superficie-2)', border: '0.5px solid var(--sn-borde)', borderRadius: 6, padding: '5px 7px' }}>
      <div style={{ fontSize: 8, color: 'var(--sn-texto-3)', textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 1 }}>{label}</div>
      <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--sn-texto)', wordBreak: 'break-word' }}>{value}</div>
    </div>
  )
);

const TabSmartOlt: React.FC<{ servicios: Servicio[] }> = ({ servicios }) => {
  const [datosOnu, setDatosOnu] = useState<Record<string, any>>({});
  const [estadoCarga, setEstadoCarga] = useState<Record<string, 'loading' | 'ok' | 'error'>>({});

  const serviciosConSn = (servicios || [])
    .map(srv => ({ srv, sn: parsePhpSerialized((srv as any).smartolt || '').sn }))
    .filter(s => s.sn);

  useEffect(() => {
    serviciosConSn.forEach(({ sn }) => {
      setEstadoCarga(prev => ({ ...prev, [sn]: 'loading' }));
      fetch(N8N_ONU_SIGNAL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sn }),
      })
        .then(r => r.json())
        .then(data => {
          const onu = data?.onus?.[0];
          if (!onu) throw new Error('sin datos');
          setDatosOnu(prev => ({ ...prev, [sn]: onu }));
          setEstadoCarga(prev => ({ ...prev, [sn]: 'ok' }));
        })
        .catch(() => setEstadoCarga(prev => ({ ...prev, [sn]: 'error' })));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(serviciosConSn.map(s => s.sn))]);

  if (serviciosConSn.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: 48, color: 'var(--sn-texto-3)' }}>
        <div style={{ fontSize: 48 }}>🔦</div>
        <p className="mt-3" style={{ fontSize: 14, fontWeight: 600, color: 'var(--sn-texto-3)' }}>SmartOLT</p>
        <p style={{ fontSize: 12 }}>Este cliente no tiene ONUs registradas.</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 12 }}>
      {serviciosConSn.map(({ sn }, i) => {
        const estado = estadoCarga[sn];
        const onu = datosOnu[sn];

        return (
          <div key={sn} style={{ background: 'var(--sn-superficie)', border: '1px solid var(--sn-borde)', borderRadius: 10, padding: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--sn-marca)', marginBottom: 6 }}>
              🔦 ONT {i + 1} · SN {sn}
            </div>

            {estado === 'loading' && <p style={{ fontSize: 12, color: 'var(--sn-texto-3)' }}>Consultando SmartOLT…</p>}
            {estado === 'error' && <p style={{ fontSize: 12, color: 'var(--sn-texto-3)' }}>No se pudo obtener información de SmartOLT.</p>}

            {estado === 'ok' && onu && (
              <>
                {/* Estado + señal, destacado arriba */}
                <div style={{
                  display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center',
                  background: onu.status?.toLowerCase() === 'online' ? 'var(--sn-exito-suave)' : 'var(--sn-peligro-suave)',
                  border: `1px solid ${onu.status?.toLowerCase() === 'online' ? 'var(--sn-exito-suave)' : 'var(--sn-peligro-suave)'}`,
                  borderRadius: 8, padding: '6px 8px', marginBottom: 8,
                }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: onu.status?.toLowerCase() === 'online' ? 'var(--sn-exito)' : 'var(--sn-peligro-texto)' }}>
                    {onu.status === 'Online' ? '🟢' : '🔴'} {onu.status} {tiempoDesde(onu.last_status_change) && `(${tiempoDesde(onu.last_status_change)})`}
                  </span>
                  {(onu.signal_1490 || onu.signal_1310) && (
                    <span style={{ fontSize: 10, color: 'var(--sn-texto-2)' }}>
                      Señal Rx: <strong>{onu.signal_1490} dBm / {onu.signal_1310} dBm</strong>
                      {onu.distance && ` (${onu.distance}m)`}
                    </span>
                  )}
                </div>

                {/* Ficha técnica */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, marginBottom: 8 }}>
                  <OltCell label="OLT" value={onu.olt_id && onu.olt_name ? `${onu.olt_id} - ${onu.olt_name}` : onu.olt_name} full />
                  <OltCell label="Tarjeta" value={onu.board} />
                  <OltCell label="Puerto" value={onu.port} />
                  <OltCell label="ONU" value={onu.onu} />
                  <OltCell label="Canal GPON" value={onu.gpon_channel || onu.pon_type} />
                  <OltCell label="SN" value={onu.sn} />
                  <OltCell label="Tipo ONU" value={onu.onu_type_name} />
                  <OltCell label="Zona" value={onu.zone_name} />
                  <OltCell label="NAP / Divisor" value={onu.odb_name} />
                  <OltCell label="Nombre" value={onu.name} full />
                  <OltCell label="Dirección" value={onu.address} full />
                  <OltCell label="Contacto" value={onu.contact} />
                  <OltCell label="Fecha autorización" value={onu.authorization_date} />
                  <OltCell label="ID externo" value={onu.unique_external_id} />
                  <OltCell label="TR069" value={onu.tr069} />
                  <OltCell label="IP de gestión" value={onu.mgmt_ip_mode} />
                  <OltCell label="VLAN" value={onu.vlan} />
                  <OltCell label="Modo ONU" value={onu.mode} />
                </div>

                {/* Perfiles de velocidad */}
                {onu.service_ports?.length > 0 && (
                  <div style={{ marginBottom: 8 }}>
                    <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--sn-texto-3)', textTransform: 'uppercase', marginBottom: 4 }}>
                      Perfiles de velocidad
                    </div>
                    {onu.service_ports.map((sp: any, k: number) => (
                      <div key={k} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
                        <OltCell label="VLAN" value={sp.vlan} />
                        <OltCell label="ID servicio" value={sp.service_port} />
                        <OltCell label="Descarga" value={formatPlan(sp.download_speed)} />
                        <OltCell label="Subida" value={formatPlan(sp.upload_speed)} />
                      </div>
                    ))}
                  </div>
                )}

                {/* Puertos Ethernet */}
                {onu.ethernet_ports?.length > 0 && (
                  <div>
                    <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--sn-texto-3)', textTransform: 'uppercase', marginBottom: 4 }}>
                      Puertos Ethernet
                    </div>
                    {onu.ethernet_ports.map((ep: any, k: number) => (
                      <div key={k} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
                        <OltCell label="Puerto" value={ep.port} />
                        <OltCell label="Estado" value={ep.admin_state} />
                        <OltCell label="Modo" value={ep.mode} />
                        <OltCell label="VLAN" value={ep.vlan} />
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ── Pestaña TR-069: datos en vivo del router (GenieACS) ──────────────────────
const N8N_ACS_OID = process.env.REACT_APP_N8N_URL_ACS_OID!;

const TabTr069: React.FC<{ servicios: Servicio[] }> = ({ servicios }) => {
  const [datosAcs, setDatosAcs] = useState<Record<string, any>>({});
  const [estadoCarga, setEstadoCarga] = useState<Record<string, 'loading' | 'ok' | 'error'>>({});
  const [seccionesAbiertas, setSeccionesAbiertas] = useState<Record<string, boolean>>({});

  const Seccion: React.FC<{ oid: string; clave: string; icono: string; titulo: string; children: React.ReactNode }> = ({ oid, clave, icono, titulo, children }) => {
    const key = `${oid}:${clave}`;
    const abierta = !!seccionesAbiertas[key];
    return (
      <div style={{ marginBottom: 8 }}>
        <button
          onClick={() => setSeccionesAbiertas(prev => ({ ...prev, [key]: !prev[key] }))}
          style={{
            width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            background: 'var(--sn-superficie-2)', border: '0.5px solid var(--sn-borde)', borderRadius: 6, padding: '7px 10px',
            fontSize: 11, fontWeight: 700, color: 'var(--sn-texto-2)', cursor: 'pointer',
          }}
        >
          <span>{icono} {titulo}</span>
          <span>{abierta ? '▲' : '▼'}</span>
        </button>
        {abierta && <div style={{ marginTop: 6 }}>{children}</div>}
      </div>
    );
  };

  const serviciosConOid = (servicios || [])
    .map(srv => {
      let personaliz: Record<string, string> = {};
      try {
        personaliz = typeof (srv as any).personalizados === 'string'
          ? JSON.parse((srv as any).personalizados)
          : (srv as any).personalizados || {};
      } catch { personaliz = {}; }
      return { srv, oid: personaliz['1770612677'] || '' };
    })
    .filter(s => s.oid);

  useEffect(() => {
    serviciosConOid.forEach(({ oid }) => {
      setEstadoCarga(prev => ({ ...prev, [oid]: 'loading' }));
      fetch(N8N_ACS_OID, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id_acs: oid }),
      })
        .then(r => r.json())
        .then(data => {
          if (data?.error || !data?.manufacturer) throw new Error(data?.mensaje || 'sin datos');

          setDatosAcs(prev => ({
            ...prev,
            [oid]: {
              manufacturer: data.manufacturer,
              model: data.model,
              hardwareVersion: data.hardwareVersion,
              softwareVersion: data.softwareVersion,
              serial: data.serial,
              productClass: data.productClass,

              mac: data.mac,
              connectionRequestURL: data.connectionRequestURL,
              udpConnReqAddress: data.udpConnReqAddress,
              lastInform: formatFecha(data.lastInform),
              lastBoot: formatFecha(data.lastBoot),
              registered: formatFecha(data.registered),

              wanAddressingType: data.wanAddressingType,
              wanIp: data.wanIp,
              wanDns: data.wanDns,
              wanStatus: data.wanStatus,
              wanUptime: data.wanUptime,

              lanIp: data.lanIp,
              lanMask: data.lanMask,
              dhcpMin: data.dhcpMin,
              dhcpMax: data.dhcpMax,
              dhcpLease: data.dhcpLease,

              cpuUso: data.cpuUso,
              memTotal: data.memTotal,
              memLibre: data.memLibre,
              memUsoPorcentaje: data.memUsoPorcentaje,

              networks: data.networks || [],
              hosts: data.hosts || [],
              totalHosts: data.totalHosts,

              bytesRecibidos: data.bytesRecibidos,
              bytesEnviados: data.bytesEnviados,
            },
          }));
          setEstadoCarga(prev => ({ ...prev, [oid]: 'ok' }));
        })
        .catch(() => setEstadoCarga(prev => ({ ...prev, [oid]: 'error' })));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(serviciosConOid.map(s => s.oid))]);

  if (serviciosConOid.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: 48, color: 'var(--sn-texto-3)' }}>
        <div style={{ fontSize: 48 }}>🔧</div>
        <p className="mt-3" style={{ fontSize: 14, fontWeight: 600, color: 'var(--sn-texto-3)' }}>TR-069</p>
        <p style={{ fontSize: 12 }}>Este cliente no tiene router con GenieACS registrado.</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 12 }}>
      {serviciosConOid.map(({ oid }, i) => {
        const estado = estadoCarga[oid];
        const info = datosAcs[oid];

        return (
          <div key={oid} style={{ background: 'var(--sn-superficie)', border: '1px solid var(--sn-borde)', borderRadius: 10, padding: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 2 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--sn-marca)' }}>
                🔧 Router {i + 1} (GenieACS)
              </div>
              <a
                href={`https://acs.sisnetel.com:3000/#!/devices/${oid}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  fontSize: 10, fontWeight: 700, color: 'var(--sn-marca)', background: 'var(--sn-marca-suave)',
                  border: '1px solid var(--sn-marca-suave)', borderRadius: 999, padding: '3px 9px', textDecoration: 'none',
                }}
              >
                🔗 Abrir en GenieACS
              </a>
            </div>
            <div style={{ fontSize: 10, color: 'var(--sn-texto-3)', marginBottom: 6 }}>
              {[info?.manufacturer, info?.model].filter(Boolean).join(' - ') || decodeDoble(oid)}
            </div>

            {estado === 'loading' && <p style={{ fontSize: 12, color: 'var(--sn-texto-3)' }}>Consultando router…</p>}
            {estado === 'error' && <p style={{ fontSize: 12, color: 'var(--sn-texto-3)' }}>Sin datos del router disponibles.</p>}

            {estado === 'ok' && info && (
              <>
                {/* 1. Redes WiFi */}
                {info.networks && info.networks.length > 0 && (
                  <Seccion oid={oid} clave="wifi" icono="📶" titulo={`Redes WiFi (${info.totalHosts ?? 0} disp.)`}>
                    {info.networks.map((n: any, k: number) => (
                      <div key={k} style={{ marginBottom: 6, background: 'var(--sn-superficie-2)', border: '0.5px solid var(--sn-borde)', borderRadius: 6, padding: '5px 7px' }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--sn-texto)', marginBottom: 3 }}>
                          📶 {n.red || `Red ${k + 1}`}: {n.ssid} {n.enabled === false ? '(deshabilitada)' : ''}
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
                          <OltCell label="Estado" value={n.status} />
                          <OltCell label="Canal" value={n.channel} />
                          <OltCell label="BSSID" value={n.bssid} />
                          <OltCell label="Potencia Tx (%)" value={n.power} />
                          <OltCell label="Dispositivos" value={`${n.count} disp.`} full />
                        </div>
                      </div>
                    ))}
                    <div style={{ fontSize: 10, color: 'var(--sn-texto-3)', paddingTop: 4, borderTop: '1px solid var(--sn-superficie-2)', display: 'flex', justifyContent: 'space-between', fontWeight: 700 }}>
                      <span>Total dispositivos conectados</span>
                      <span>{info.totalHosts ?? 0} disp.</span>
                    </div>
                  </Seccion>
                )}

                {/* 2. Identificación del equipo */}
                <Seccion oid={oid} clave="id" icono="🏷️" titulo="Identificación del equipo">
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
                    <OltCell label="Fabricante" value={info.manufacturer} />
                    <OltCell label="Modelo" value={info.model} />
                    <OltCell label="Versión hardware" value={info.hardwareVersion} />
                    <OltCell label="Versión software" value={info.softwareVersion} />
                    <OltCell label="Serie" value={info.serial} />
                    <OltCell label="Clase de producto" value={info.productClass} />
                  </div>
                </Seccion>

                {/* 2.5 Rendimiento (no todos los equipos lo reportan) */}
                {(info.cpuUso != null || info.memTotal != null || info.memUsoPorcentaje != null) && (
                  <Seccion oid={oid} clave="rendimiento" icono="⚙️" titulo="Rendimiento">
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
                      <OltCell label="CPU" value={info.cpuUso != null ? `${info.cpuUso}%` : undefined} />
                      <OltCell label="Memoria usada" value={info.memUsoPorcentaje != null ? `${info.memUsoPorcentaje}%` : undefined} />
                      <OltCell label="Memoria total" value={info.memTotal} />
                      <OltCell label="Memoria libre" value={info.memLibre} />
                    </div>
                  </Seccion>
                )}

                {/* 3. Gestión / GenieACS */}
                <Seccion oid={oid} clave="gestion" icono="🛠️" titulo="Gestión (GenieACS)">
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
                    <OltCell label="MAC de gestión" value={info.mac} />
                    <OltCell label="URL conexión TR-069" value={info.connectionRequestURL} full />
                    <OltCell label="Dirección UDP conexión" value={info.udpConnReqAddress} full />
                    <OltCell label="Últ. conexión" value={info.lastInform} />
                    <OltCell label="Últ. reinicio" value={info.lastBoot} />
                    <OltCell label="Registrado en GenieACS" value={info.registered} />
                  </div>
                </Seccion>

                {/* 4. WAN */}
                <Seccion oid={oid} clave="wan" icono="🌐" titulo="WAN (internet)">
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
                    <OltCell label="Direccionamiento" value={info.wanAddressingType} />
                    <OltCell label="IP externa" value={info.wanIp} />
                    <OltCell label="DNS" value={info.wanDns} full />
                    <OltCell label="Estado conexión" value={info.wanStatus} />
                    <OltCell label="Uptime" value={info.wanUptime} />
                  </div>
                </Seccion>

                {/* 5. LAN */}
                <Seccion oid={oid} clave="lan" icono="🏠" titulo="LAN (red interna)">
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
                    <OltCell label="IP del router" value={info.lanIp} />
                    <OltCell label="Máscara subred" value={info.lanMask} />
                    <OltCell label="Rango DHCP" value={info.dhcpMin && info.dhcpMax ? `${info.dhcpMin} - ${info.dhcpMax}` : undefined} full />
                    <OltCell label="Concesión DHCP (seg)" value={info.dhcpLease} />
                  </div>
                </Seccion>

                {/* 6. Dispositivos conectados (detalle) */}
                {info.hosts && info.hosts.length > 0 && (
                  <Seccion oid={oid} clave="hosts" icono="📱" titulo={`Dispositivos conectados (${info.hosts.length})`}>
                    {info.hosts.map((h: any, k: number) => (
                      <div key={k} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, marginBottom: 4 }}>
                        <OltCell label="Nombre" value={`${h.activo === false ? '⚪' : '🟢'} ${h.nombre}`} full />
                        <OltCell label="IP" value={h.ip} />
                        <OltCell label="MAC" value={h.mac} />
                        <OltCell label="Interfaz" value={h.tipo} />
                      </div>
                    ))}
                  </Seccion>
                )}

                {/* 7. Tráfico */}
                {(info.bytesRecibidos || info.bytesEnviados) && (
                  <Seccion oid={oid} clave="trafico" icono="📊" titulo="Tráfico (puerto LAN)">
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
                      <OltCell label="Recibidos" value={formatBytes(info.bytesRecibidos)} />
                      <OltCell label="Enviados" value={formatBytes(info.bytesEnviados)} />
                    </div>
                  </Seccion>
                )}
              </>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ── Resumen técnico: mismas tarjetas compactas del Menú (GenieACS + ONT) con carrusel ──
const CLAVE_OID_GENIEACS = '1770612677';

/**
 * Clave donde se guarda el cliente que estaba abierto antes de una recarga
 * completa. El detalle del cliente es estado de React (no una ruta), así que
 * sin esto un window.location.reload() devolvería al usuario a la lista.
 */
/** Recarga toda la página conservando el cliente que se estaba viendo. */
function recargarPaginaConservandoDetalle(detalle: unknown) {
  guardarDetalleAbierto(detalle);
  window.location.reload();
}

const ResumenTecnico: React.FC<{
  servicios: Servicio[];
  clienteNombre: string;
  infoSlide?: React.ReactNode;
  cedula?: string;
  mostrarActivar?: boolean;
  /** Se llama justo antes de salir a otra pantalla, para que al volver se reabra este cliente. */
  alSalir?: () => void;
}> = ({ servicios, clienteNombre, infoSlide, cedula, mostrarActivar, alSalir }) => {
  const navigate = useNavigate();
  type AcsInfo = {
    loading: boolean; error: boolean;
    manufacturer?: string; model?: string; serial?: string; software?: string; mac?: string;
    lastInform?: string; lastBoot?: string;
    networks?: { ssid: string; count: number }[]; totalHosts?: number;
  };
  type OnuSignal = {
    loading: boolean; error: boolean;
    status?: string; signal?: string; signal1490?: number; signal1310?: number;
    oltName?: string; clientName?: string; distanceKm?: string; lastChange?: string;
    plan?: string; onuType?: string; port?: string;
  };

  const [acsInfos, setAcsInfos] = useState<Record<string, AcsInfo>>({});
  const [acsExpandido, setAcsExpandido] = useState<Record<string, boolean>>({});
  const [onuSignals, setOnuSignals] = useState<Record<string, OnuSignal>>({});
  const [ontExpandido, setOntExpandido] = useState<Record<string, boolean>>({});
  const [activeIndex, setActiveIndex] = useState(0);
  const carouselRef = useRef<HTMLDivElement | null>(null);

  const getOid = (srv: Servicio): string => {
    try {
      const personalizados = typeof srv.personalizados === 'string' ? JSON.parse(srv.personalizados) : (srv.personalizados || {});
      return personalizados[CLAVE_OID_GENIEACS] || '';
    } catch { return ''; }
  };

  const serviciosConOid = servicios.map(srv => ({ oid: getOid(srv) })).filter(s => s.oid);
  const serviciosConSn = servicios.map(srv => ({ sn: parsePhpSerialized((srv as any).smartolt || '').sn })).filter(s => s.sn);

  useEffect(() => {
    const url = process.env.REACT_APP_N8N_URL_ACS_OID;
    if (!url) return;
    serviciosConOid.forEach(({ oid }) => {
      setAcsInfos(prev => ({ ...prev, [oid]: { loading: true, error: false } }));
      fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id_acs: oid }) })
        .then(r => r.json())
        .then(data => {
          if (data?.error || !data?.manufacturer) throw new Error('sin datos');
          setAcsInfos(prev => ({
            ...prev,
            [oid]: {
              loading: false, error: false,
              manufacturer: data.manufacturer, model: data.model, serial: data.serial,
              software: data.softwareVersion, mac: data.mac,
              lastInform: formatFecha(data.lastInform), lastBoot: formatFecha(data.lastBoot),
              networks: (data.networks || []).map((n: any) => ({ ssid: n.ssid, count: n.count })),
              totalHosts: data.totalHosts,
            },
          }));
        })
        .catch(() => setAcsInfos(prev => ({ ...prev, [oid]: { loading: false, error: true } })));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(serviciosConOid.map(s => s.oid))]);

  useEffect(() => {
    if (!N8N_ONU_SIGNAL) return;
    serviciosConSn.forEach(({ sn }) => {
      setOnuSignals(prev => ({ ...prev, [sn]: { loading: true, error: false } }));
      fetch(N8N_ONU_SIGNAL, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ sn }) })
        .then(r => r.json())
        .then(data => {
          const onu = data?.onus?.[0];
          if (!onu) throw new Error('sin datos');
          setOnuSignals(prev => ({
            ...prev,
            [sn]: {
              loading: false, error: false,
              status: onu.status, signal: onu.signal,
              signal1490: onu.signal_1490 !== undefined ? parseFloat(onu.signal_1490) : undefined,
              signal1310: onu.signal_1310 !== undefined ? parseFloat(onu.signal_1310) : undefined,
              oltName: onu.olt_name, clientName: onu.name,
              distanceKm: onu.distance ? `${(parseFloat(onu.distance) / 1000).toFixed(1)} km` : undefined,
              lastChange: formatFecha(onu.last_status_change),
              plan: formatPlan(onu.service_ports?.[0]?.download_speed),
              onuType: onu.onu_type_name, port: onu.port,
            },
          }));
        })
        .catch(() => setOnuSignals(prev => ({ ...prev, [sn]: { loading: false, error: true } })));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(serviciosConSn.map(s => s.sn))]);

  const totalSlides = (infoSlide ? 1 : 0) + serviciosConOid.length + serviciosConSn.length;
  if (totalSlides === 0) return null;
  const esCarrusel = totalSlides > 1;

  const cardStyle = {
    background: 'var(--sn-superficie)', borderRadius: 14, border: '1px solid var(--sn-borde)', padding: '10px 14px',
    ...(esCarrusel ? { flex: '0 0 88%', scrollSnapAlign: 'start' as const } : {}),
  };

  const renderAcs = (oid: string, i: number) => {
    const info = acsInfos[oid];
    const expandido = !!acsExpandido[oid];
    return (
      <div key={`acs-${oid}`} style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8, marginBottom: 6 }}>
          <span style={{ fontWeight: 700, fontSize: 13, color: 'var(--sn-marca-fuerte)' }}>📡 Router (GenieACS)</span>
          <span style={{ fontSize: 10, color: 'var(--sn-texto-3)', textAlign: 'right' as const, whiteSpace: 'nowrap' as const, overflow: 'hidden', textOverflow: 'ellipsis' }}>
            Servicio {i + 1} · {[info?.manufacturer, info?.model].filter(Boolean).join(' - ') || decodeDoble(oid)}
          </span>
        </div>
        {info && !info.loading && !info.error && (
          <div style={{ fontSize: 10, color: 'var(--sn-texto-3)', marginBottom: 6, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' as const }}>
              {info.networks?.filter(n => n.ssid).map((n, k) => (
                <span key={k}>📶 <span style={{ color: 'var(--sn-texto)', fontWeight: 500 }}>{n.ssid}</span></span>
              ))}
            </div>
            <span style={{ whiteSpace: 'nowrap' as const }}>{info.totalHosts ?? 0} disp. conectados</span>
          </div>
        )}

        {!info || info.loading ? (
          <div style={{ fontSize: 12, color: 'var(--sn-texto-3)' }}>Consultando router…</div>
        ) : info.error ? (
          <div style={{ fontSize: 12, color: 'var(--sn-texto-3)' }}>Sin datos del router disponibles.</div>
        ) : (
          <>
            {expandido && (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '4px 6px', marginBottom: info.networks?.length ? 8 : 0 }}>
                  {[
                    { label: 'Serie', value: info.serial },
                    { label: 'Software', value: info.software },
                    { label: 'Gestión', value: info.mac },
                    { label: 'Últ. conexión', value: info.lastInform },
                    { label: 'Últ. reinicio', value: info.lastBoot },
                  ].filter(s => s.value).map((s, k) => (
                    <div key={k} style={{ fontSize: 10, color: 'var(--sn-texto-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {s.label}<br /><span style={{ color: 'var(--sn-texto)', fontWeight: 500, fontSize: 11 }}>{s.value}</span>
                    </div>
                  ))}
                </div>

                {info.networks && info.networks.length > 0 && (
                  <div style={{ marginBottom: 6 }}>
                    <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--sn-texto-3)', textTransform: 'uppercase', marginBottom: 4 }}>
                      Redes WiFi
                    </div>
                    {info.networks.map((n, k) => (
                      <div key={k} style={{ fontSize: 10, color: 'var(--sn-texto-3)', marginTop: 2, display: 'flex', justifyContent: 'space-between' }}>
                        <span>📶 <span style={{ color: 'var(--sn-texto)', fontWeight: 500 }}>{n.ssid}</span></span>
                        <span>{n.count} disp.</span>
                      </div>
                    ))}
                    <div style={{ fontSize: 10, color: 'var(--sn-texto-3)', marginTop: 4, paddingTop: 4, borderTop: '1px solid var(--sn-superficie-2)', display: 'flex', justifyContent: 'space-between', fontWeight: 700 }}>
                      <span>Total</span>
                      <span>{info.totalHosts ?? 0} disp.</span>
                    </div>
                  </div>
                )}
              </>
            )}

            <button
              onClick={() => setAcsExpandido(prev => ({ ...prev, [oid]: !prev[oid] }))}
              style={{
                width: '100%', background: 'var(--sn-superficie-2)', border: '1px solid var(--sn-borde)', borderRadius: 6,
                padding: '5px 8px', fontSize: 10, fontWeight: 700, color: 'var(--sn-marca)', cursor: 'pointer',
              }}
            >
              {expandido ? '▲ Ver menos' : '▼ Ver más'}
            </button>
          </>
        )}
      </div>
    );
  };

  const renderOnt = (sn: string, i: number) => {
    const sig = onuSignals[sn];
    const enRango = sig?.signal1490 !== undefined && sig.signal1490 <= -8 && sig.signal1490 >= -27;
    const esOk = sig?.status?.toLowerCase() === 'online' && enRango;

    const statsSecundarios = sig && !sig.loading && !sig.error ? [
      { label: 'Estado', value: sig.status },
      { label: 'Señal', value: sig.signal },
      { label: 'Plan', value: sig.plan },
      { label: 'RX (1490)', value: sig.signal1490 !== undefined ? `${sig.signal1490} dBm` : undefined },
      { label: 'TX (1310)', value: sig.signal1310 !== undefined ? `${sig.signal1310} dBm` : undefined },
      { label: 'OLT', value: sig.oltName },
      { label: 'Modelo ONU', value: sig.onuType },
      { label: 'Puerto', value: sig.port },
      { label: 'Distancia', value: sig.distanceKm },
      { label: 'Últ. conexión', value: sig.lastChange },
    ].filter(s => s.value) : [];

    const expandidoOnt = !!ontExpandido[sn];

    return (
      <div key={`ont-${sn}`} style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8, marginBottom: 6 }}>
          <span style={{ fontWeight: 700, fontSize: 13, color: 'var(--sn-marca-fuerte)' }}>📶 ONT</span>
          <span style={{ fontSize: 10, color: 'var(--sn-texto-3)', textAlign: 'right' as const, whiteSpace: 'nowrap' as const, overflow: 'hidden', textOverflow: 'ellipsis' }}>
            SN {sn}
          </span>
        </div>

        <div style={{ fontSize: 11, color: 'var(--sn-texto-3)', marginBottom: 6, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <span>{sig?.clientName || clienteNombre}</span>
          {sig && !sig.loading && !sig.error && (
            <span style={{
              display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, fontWeight: 700,
              color: esOk ? 'var(--sn-exito)' : 'var(--sn-peligro-texto)', background: esOk ? 'var(--sn-exito-suave)' : 'var(--sn-peligro-suave)',
              border: `1px solid ${esOk ? 'var(--sn-exito-suave)' : 'var(--sn-peligro-suave)'}`, borderRadius: 999, padding: '2px 8px',
              whiteSpace: 'nowrap' as const,
            }}>
              {esOk ? '✅ OK' : '⚠️ Alerta'}
            </span>
          )}
        </div>

        {!sig || sig.loading ? (
          <div style={{ fontSize: 12, color: 'var(--sn-texto-3)' }}>Consultando señal…</div>
        ) : sig.error ? (
          <div style={{ fontSize: 12, color: 'var(--sn-texto-3)' }}>Sin datos de señal disponibles.</div>
        ) : (
          <>
            {expandidoOnt && statsSecundarios.length > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '4px 6px', marginBottom: 8 }}>
                {statsSecundarios.map((s, k) => (
                  <div key={k} style={{ fontSize: 10, color: 'var(--sn-texto-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {s.label}<br /><span style={{ color: 'var(--sn-texto)', fontWeight: 500, fontSize: 11 }}>{s.value}</span>
                  </div>
                ))}
              </div>
            )}

            {statsSecundarios.length > 0 && (
              <button
                onClick={() => setOntExpandido(prev => ({ ...prev, [sn]: !prev[sn] }))}
                style={{
                  width: '100%', background: 'var(--sn-superficie-2)', border: '1px solid var(--sn-borde)', borderRadius: 6,
                  padding: '5px 8px', fontSize: 10, fontWeight: 700, color: 'var(--sn-marca)', cursor: 'pointer',
                }}
              >
                {expandidoOnt ? '▲ Ver menos' : '▼ Ver más'}
              </button>
            )}
          </>
        )}
      </div>
    );
  };

  const slides = [
    ...(infoSlide ? [<div key="info-slide" style={cardStyle}>{infoSlide}</div>] : []),
    ...serviciosConOid.map((s, i) => renderAcs(s.oid, i)),
    ...serviciosConSn.map((s, i) => renderOnt(s.sn, i)),
  ];

  const irASlide = (idx: number) => {
    const el = carouselRef.current;
    const card = el?.children[idx] as HTMLElement | undefined;
    if (!card) return;
    card.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
    setActiveIndex(idx);
  };

  const handleScroll = () => {
    const el = carouselRef.current;
    if (!el) return;
    const cardWidth = el.children[0]?.clientWidth || 1;
    const gap = 10;
    setActiveIndex(Math.round(el.scrollLeft / (cardWidth + gap)));
  };

  // Accesos rápidos: Dispositivos/Cambiar Contraseña solo si el cliente tiene GenieACS;
  // "Activar Servicio" solo si está suspendido y con deuda (independiente de GenieACS).
  // Los datos del cliente van por navigate state (el admin no tiene su propia sesión con estos datos).
  const botonesAcs = (serviciosConOid.length > 0 || mostrarActivar) && (
    <div style={{ display: 'flex', justifyContent: 'flex-start', gap: 8, marginTop: 10, flexWrap: 'wrap' as const }}>
      {serviciosConOid.length > 0 && (
        <>
          <button
            onClick={() => { alSalir?.(); navigate('/menu/dispositivos', { state: { servicios } }); }}
            style={{
              background: 'var(--sn-superficie)', border: '1px solid var(--sn-borde)', borderRadius: 10,
              padding: '8px 10px', cursor: 'pointer', width: 72,
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4,
            }}
          >
            <FaMobileAlt size={18} color="var(--sn-marca)" />
            <span style={{ fontSize: 9, fontWeight: 700, color: 'var(--sn-marca)', textAlign: 'center' as const, lineHeight: 1.2 }}>
              Dispositivos<br />Conectados
            </span>
          </button>
          <button
            onClick={() => { alSalir?.(); navigate('/menu/wifi', { state: { servicios } }); }}
            style={{
              background: 'var(--sn-superficie)', border: '1px solid var(--sn-borde)', borderRadius: 10,
              padding: '8px 10px', cursor: 'pointer', width: 72,
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4,
            }}
          >
            <FaKey size={18} color="var(--sn-marca)" />
            <span style={{ fontSize: 9, fontWeight: 700, color: 'var(--sn-marca)', textAlign: 'center' as const, lineHeight: 1.2 }}>
              Cambiar<br />Contraseña
            </span>
          </button>
        </>
      )}
      {mostrarActivar && (
        <button
          onClick={() => { alSalir?.(); navigate('/menu/promesa', { state: { cedula } }); }}
          style={{
            background: 'var(--sn-exito-suave)', border: '1px solid var(--sn-exito)', borderRadius: 10,
            padding: '8px 10px', cursor: 'pointer', width: 72,
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4,
          }}
        >
          <FaPlug size={18} color="var(--sn-exito)" />
          <span style={{ fontSize: 9, fontWeight: 700, color: 'var(--sn-exito)', textAlign: 'center' as const, lineHeight: 1.2 }}>
            Activar<br />Servicio
          </span>
        </button>
      )}
    </div>
  );

  if (!esCarrusel) return <div style={{ marginBottom: 12 }}>{slides[0]}{botonesAcs}</div>;

  return (
    <div style={{ marginBottom: 12 }}>
      <div
        ref={carouselRef}
        onScroll={handleScroll}
        className="resumen-tecnico-carousel"
        style={{
          display: 'flex', alignItems: 'flex-start', gap: 10, overflowX: 'auto', scrollSnapType: 'x mandatory',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {slides}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginTop: 8 }}>
        <button
          onClick={() => irASlide(Math.max(0, activeIndex - 1))}
          disabled={activeIndex === 0}
          aria-label="Anterior"
          style={{ border: 'none', background: 'none', padding: 2, fontSize: 12, lineHeight: 1, color: activeIndex === 0 ? 'var(--sn-borde-fuerte)' : 'var(--sn-marca)', cursor: activeIndex === 0 ? 'default' : 'pointer' }}
        >
          ◀
        </button>
        <div style={{ display: 'flex', gap: 5 }}>
          {slides.map((_, i) => (
            <span
              key={i}
              onClick={() => irASlide(i)}
              style={{ width: 6, height: 6, borderRadius: '50%', cursor: 'pointer', background: i === activeIndex ? 'var(--sn-marca)' : 'var(--sn-borde-fuerte)' }}
            />
          ))}
        </div>
        <button
          onClick={() => irASlide(Math.min(slides.length - 1, activeIndex + 1))}
          disabled={activeIndex === slides.length - 1}
          aria-label="Siguiente"
          style={{ border: 'none', background: 'none', padding: 2, fontSize: 12, lineHeight: 1, color: activeIndex === slides.length - 1 ? 'var(--sn-borde-fuerte)' : 'var(--sn-marca)', cursor: activeIndex === slides.length - 1 ? 'default' : 'pointer' }}
        >
          ▶
        </button>
      </div>
      {botonesAcs}
    </div>
  );
};

// ── Pestaña próximamente ──────────────────────────────────────────────────────
const TabProximamente: React.FC<{ label: string; icon: string }> = ({ label, icon }) => (
  <div style={{ textAlign: 'center', padding: 48, color: 'var(--sn-texto-3)' }}>
    <div style={{ fontSize: 48 }}>{icon}</div>
    <p className="mt-3" style={{ fontSize: 14, fontWeight: 600, color: 'var(--sn-texto-3)' }}>{label}</p>
    <p style={{ fontSize: 12 }}>Próximamente disponible</p>
  </div>
);

// ── Componente: pantalla de detalle de un cliente ─────────────────────────────
const DetalleCliente: React.FC<{
  clienteId: number; cedula: string; nombre: string; estado: string;
  onBack: () => void; onRecargar: () => void;
  /** Antes de ir a Dispositivos / Contraseña / Promesa: recuerda este cliente para reabrirlo al volver. */
  onSalir: () => void;
}> = ({ clienteId, cedula, nombre, estado, onBack, onRecargar, onSalir }) => {
  const [datos,   setDatos]   = useState<ClienteDetalle | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error,   setError]   = useState('');
  const [tab,     setTab]     = useState<TabId>('servicios');
  const [infoExpandida, setInfoExpandida] = useState(false);

  const cargarCliente = (esRefresh: boolean) => {
    if (esRefresh) setRefreshing(true); else setLoading(true);
    setError('');
    fetch(N8N_CLIENTE_CEDULA, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ cedula }),
    })
      .then(r => r.json())
      .then(data => {
        const raw     = data.datos ?? data;
        const cliente = Array.isArray(raw) ? raw[0] : raw;
        if (cliente && (cliente.id || cliente.nombre)) {
          setDatos(cliente);
        } else {
          setError('No se pudieron cargar los datos del cliente.');
        }
      })
      .catch(() => setError('Error de conexión.'))
      .finally(() => { setLoading(false); setRefreshing(false); });
  };

  useEffect(() => {
    cargarCliente(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cedula]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
      <AppNavbar />
      <div style={{ flex: 1, overflowY: 'auto' as const }}>

        {/* Encabezado */}
        <div style={{ background: 'var(--sn-superficie)', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid var(--sn-borde)', position: 'sticky', top: 0, zIndex: 10 }}>
          <button onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: 'var(--sn-accion)' }}>
            <FaArrowLeft size={18} />
          </button>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 15 }}>{nombre}</div>
            <div style={{ marginTop: 2 }}>{estadoBadge(estado)}</div>
          </div>
          <button
            onClick={onRecargar}
            disabled={loading || refreshing}
            style={{
              background: 'var(--sn-marca-suave)', border: '1px solid var(--sn-marca-suave)', borderRadius: 999,
              padding: '6px 12px', cursor: (loading || refreshing) ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700, color: 'var(--sn-marca)',
            }}
          >
            {refreshing ? <Spinner animation="border" size="sm" /> : <span style={{ fontSize: 14 }}>🔄</span>}
            Actualizar
          </button>
        </div>

        {loading && (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Spinner animation="border" variant="primary" size="sm" />
            <p className="mt-2 text-muted" style={{ fontSize: 13 }}>Cargando datos...</p>
          </div>
        )}
        {error && <p style={{ color: 'var(--sn-peligro)', padding: 20, textAlign: 'center' }}>{error}</p>}

        {datos && (
          <>
            {/* Carrusel: Información personal (primero) + Router (GenieACS) + ONT, todo junto */}
            <div style={{ padding: '6px 10px 0' }}>
              <ResumenTecnico
                servicios={datos.servicios || []}
                clienteNombre={datos.nombre}
                cedula={datos.cedula}
                alSalir={onSalir}
                mostrarActivar={datos.estado?.toUpperCase() === 'SUSPENDIDO' && (datos.facturacion?.facturas_nopagadas || 0) > 0}
                infoSlide={
                  <>
                    <div style={{ fontSize: 8, fontWeight: 700, color: 'var(--sn-texto-3)', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 4 }}>
                      Información personal
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 8px' }}>
                      <InfoRowCompact icon={<FaIdCard size={8} />}    label="Cédula" value={datos.cedula} copiable />
                      <InfoRowCompact icon={<FaMobileAlt size={8} />} label="Móvil"  value={datos.movil} />
                    </div>

                    {infoExpandida && (
                      <>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 8px' }}>
                          <InfoRowCompact icon={<FaPhone size={8} />}  label="Teléfono"   value={datos.telefono} />
                          <InfoRowCompact icon={<FaIdCard size={8} />} label="ID Cliente" value={String(datos.id)} />
                        </div>
                        <InfoRowCompact icon={<FaEnvelope size={8} />}     label="Correo"    value={datos.correo} />
                        <InfoRowCompact icon={<FaMapMarkerAlt size={8} />} label="Dirección" value={datos.direccion_principal} />
                        {datos.fecha_suspendido && !datos.fecha_suspendido.startsWith('0000') && (
                          <InfoRowCompact
                            icon={<FaCalendarAlt size={8} />}
                            label={datos.estado?.toUpperCase() === 'SUSPENDIDO' ? 'Suspendido' : 'Fecha corte'}
                            value={datos.fecha_suspendido.split(' ')[0]}
                          />
                        )}
                        {datos.Promesa_Pago && datos.Promesa_Pago !== '0000-00-00' && (
                          <InfoRowCompact icon={<FaCalendarAlt size={8} />} label="Promesa pago" value={datos.Promesa_Pago} />
                        )}
                      </>
                    )}

                    <button
                      type="button"
                      onClick={() => setInfoExpandida(v => !v)}
                      style={{
                        width: '100%', marginTop: 4, background: 'var(--sn-superficie-2)', border: '1px solid var(--sn-borde)', borderRadius: 6,
                        padding: '3px 6px', fontSize: 9, fontWeight: 700, color: 'var(--sn-marca)', cursor: 'pointer',
                      }}
                    >
                      {infoExpandida ? '▲ Ver menos' : '▼ Ver más'}
                    </button>
                  </>
                }
              />

              {datos.facturacion && (
                <div style={{
                  background: datos.facturacion.facturas_nopagadas > 0 ? 'var(--sn-alerta-suave)' : 'var(--sn-exito-suave)',
                  borderRadius: 8,
                  border: `1px solid ${datos.facturacion.facturas_nopagadas > 0 ? 'var(--sn-alerta-suave)' : 'var(--sn-exito-suave)'}`,
                  padding: '5px 10px', marginBottom: 6,
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                }}>
                  <div>
                    <div style={{ fontSize: 9, color: 'var(--sn-texto-3)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Pendientes</div>
                    <div style={{ fontWeight: 700, fontSize: 14, color: datos.facturacion.facturas_nopagadas > 0 ? 'var(--sn-alerta-texto)' : 'var(--sn-exito)' }}>
                      {datos.facturacion.facturas_nopagadas}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 9, color: 'var(--sn-texto-3)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Total deuda</div>
                    <div style={{ fontWeight: 700, fontSize: 14, color: datos.facturacion.facturas_nopagadas > 0 ? 'var(--sn-alerta-texto)' : 'var(--sn-exito)' }}>
                      ${datos.facturacion.total_facturas}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Pestañas */}
            <div style={{ position: 'sticky', top: 57, zIndex: 9, background: 'var(--sn-superficie)', borderBottom: '1px solid var(--sn-borde)' }}>
              <div style={{ display: 'flex', overflowX: 'auto' as const }}>
                {TABS.map(t => (
                  <button key={t.id} onClick={() => setTab(t.id)} style={{
                    flex: 1, minWidth: 72, border: 'none', background: 'none',
                    padding: '10px 4px 8px', cursor: 'pointer', fontSize: 11, fontWeight: 600,
                    color: tab === t.id ? 'var(--sn-accion)' : 'var(--sn-texto-3)',
                    borderBottom: tab === t.id ? '2px solid var(--sn-accion)' : '2px solid transparent',
                    whiteSpace: 'nowrap' as const,
                  }}>
                    <div style={{ fontSize: 16, marginBottom: 2 }}>{t.icon}</div>
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Contenido de cada pestaña */}
            {tab === 'servicios' && (
              <div style={{ padding: '12px 14px' }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--sn-texto-3)', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 8 }}>
                  Servicios ({datos.servicios?.length || 0}) — toca para ver detalles
                </div>
                {datos.servicios?.length > 0
                  ? datos.servicios.map((srv, i) => <ServicioCard key={srv.id} srv={srv} idx={i} onChangeTab={setTab} />)
                  : <p style={{ color: 'var(--sn-texto-3)', fontSize: 13 }}>Sin servicios registrados.</p>
                }
              </div>
            )}

            {tab === 'facturas'  && <TabFacturas clienteId={clienteId} cedula={cedula} nombre={nombre} />}
            {tab === 'smartolt'  && <TabSmartOlt servicios={datos.servicios || []} />}
            {tab === 'tr069'     && <TabTr069 servicios={datos.servicios || []} />}
          </>
        )}
      </div>
      <BottomNav />
    </div>
  );
};

// ── Componente principal: lista de clientes ───────────────────────────────────
/**
 * Muestra la lista paginada de todos los clientes.
 * Si el usuario toca un cliente, cambia a la vista de detalle (DetalleCliente).
 */
const Clientes: React.FC = () => {
  const navigate = useNavigate();

  // Recuperamos los datos del usuario logueado para verificar si es admin
  const clientDataJSON = localStorage.getItem('clientData');
  const clientData = clientDataJSON ? JSON.parse(clientDataJSON) : null;

  // ── Estados ──────────────────────────────────────────────────────────────────
  const [clientes, setClientes] = useState<ClienteLista[]>([]);   // Lista completa de clientes
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busqueda, setBusqueda] = useState('');          // Texto del campo de búsqueda
  const [filtroEstado, setFiltroEstado] = useState('TODOS'); // Filtro activo
  // Cliente seleccionado para ver detalle. Si venimos de una recarga completa
  // (botón "Actualizar"), se restaura el que estaba abierto para no perderlo.
  // OJO: el inicializador debe ser puro. En React StrictMode se ejecuta dos
  // veces, así que borrar aquí la clave haría que la segunda pasada no
  // encontrara nada y se perdiera el detalle. El borrado va en un efecto.
  const [detalle, setDetalle] = useState<ClienteLista | null>(() => leerDetalleAbierto<ClienteLista>());

  // La restauración es de un solo uso: se limpia tras montar.
  useEffect(() => {
    olvidarDetalleAbierto();
  }, []);
  const [pageSize, setPageSize] = useState(25);          // Cuántos por página (25, 50, 100, 0=todos)
  const [pagina, setPagina] = useState(1);               // Página actual

  // Cargar la lista de clientes al entrar a la pantalla
  useEffect(() => {
    // Si no es admin, redirigir al menú
    if (!clientData?.token_cliente) { navigate('/menu'); return; }

    fetch(N8N_CLIENTES_TODOS, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    })
      .then(r => r.json())
      .then(data => {
        if (data.estado === 'exito') setClientes(data.clientes);
        else setError('No se pudo cargar la lista de clientes.');
      })
      .catch(() => setError('Error de conexión al obtener clientes.'))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Cálculos derivados (useMemo evita recalcular si no cambiaron las dependencias) ──

  /** Lista filtrada por estado y búsqueda de texto */
  const filtrados = useMemo(() => {
    let lista = clientes;
    // Primero filtramos por estado (Activo, Suspendido, etc.)
    if (filtroEstado !== 'TODOS') lista = lista.filter(c => c.estado?.toUpperCase() === filtroEstado);
    // Luego filtramos por texto de búsqueda
    const q = busqueda.toLowerCase().trim();
    if (q) lista = lista.filter(c =>
      c.nombre?.toLowerCase().includes(q) ||
      c.cedula?.toLowerCase().includes(q) ||
      c.movil?.toLowerCase().includes(q) ||
      String(c.id).includes(q)
    );
    return lista;
  }, [clientes, filtroEstado, busqueda]);

  const totalPaginas = pageSize === 0 ? 1 : Math.ceil(filtrados.length / pageSize);
  const paginaActual = Math.min(pagina, totalPaginas || 1); // Evita que la página quede fuera de rango

  /** Clientes de la página actual */
  const paginados = useMemo(() => {
    if (pageSize === 0) return filtrados; // "Todos" = sin paginar
    const start = (paginaActual - 1) * pageSize;
    return filtrados.slice(start, start + pageSize);
  }, [filtrados, pageSize, paginaActual]);

  /** Contadores por estado para los chips del encabezado */
  const contadores = useMemo(() => ({
    ACTIVO:     clientes.filter(c => c.estado?.toUpperCase() === 'ACTIVO').length,
    SUSPENDIDO: clientes.filter(c => c.estado?.toUpperCase() === 'SUSPENDIDO').length,
    RETIRADO:   clientes.filter(c => c.estado?.toUpperCase() === 'RETIRADO').length,
  }), [clientes]);

  // useCallback evita recrear estas funciones en cada render
  const handleFiltroEstado = useCallback((v: string) => { setFiltroEstado(v); setPagina(1); }, []);
  const handleBusqueda     = useCallback((v: string) => { setBusqueda(v); setPagina(1); }, []);
  const handlePageSize     = useCallback((v: number) => { setPageSize(v); setPagina(1); }, []);

  // Si hay un cliente seleccionado, mostrar la pantalla de detalle
  if (detalle) {
    return (
      <DetalleCliente
        clienteId={detalle.id}
        cedula={detalle.cedula}
        nombre={detalle.nombre}
        estado={detalle.estado}
        onBack={() => setDetalle(null)}
        onRecargar={() => recargarPaginaConservandoDetalle(detalle)}
        onSalir={() => guardarDetalleAbierto(detalle)}
      />
    );
  }

  // ── Render de la lista ───────────────────────────────────────────────────────
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
      <AppNavbar />
      <div style={{ flex: 1, overflowY: 'auto' as const }}>

        {/* ── Encabezado fijo (no se mueve al hacer scroll) ── */}
        <div style={{ background: 'var(--sn-superficie)', borderBottom: '1px solid var(--sn-borde)', padding: '12px 14px', position: 'sticky', top: 0, zIndex: 10 }}>
          {/* Título con botón de regreso y contador */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
            <button onClick={() => navigate('/menu')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: 'var(--sn-accion)' }}>
              <FaArrowLeft size={18} />
            </button>
            <h5 style={{ margin: 0, fontWeight: 700, fontSize: 16 }}>Mis Clientes</h5>
            {!loading && (
              <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--sn-texto-3)' }}>
                {filtrados.length} de {clientes.length}
              </span>
            )}
          </div>

          {/* Chips con contadores por estado */}
          {!loading && !error && (
            <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
              <span style={{ background: 'var(--sn-exito-suave)', color: 'var(--sn-exito-texto)', borderRadius: 20, padding: '2px 10px', fontSize: 11, fontWeight: 600 }}>✅ {contadores.ACTIVO}</span>
              <span style={{ background: 'var(--sn-alerta-suave)', color: 'var(--sn-alerta-texto)', borderRadius: 20, padding: '2px 10px', fontSize: 11, fontWeight: 600 }}>⏸ {contadores.SUSPENDIDO}</span>
              <span style={{ background: 'var(--sn-peligro-suave)', color: 'var(--sn-peligro-texto)', borderRadius: 20, padding: '2px 10px', fontSize: 11, fontWeight: 600 }}>❌ {contadores.RETIRADO}</span>
            </div>
          )}

          {/* Campo de búsqueda */}
          <InputGroup size="sm" className="mb-2">
            <InputGroup.Text style={{ background: 'var(--sn-superficie-2)' }}>
              <FaSearch size={13} color="var(--sn-texto-3)" />
            </InputGroup.Text>
            <Form.Control
              placeholder="Nombre, cédula, móvil, ID..."
              value={busqueda}
              onChange={e => handleBusqueda(e.target.value)}
            />
          </InputGroup>

          {/* Botones de filtro por estado */}
          <div style={{ display: 'flex', gap: 5, overflowX: 'auto', paddingBottom: 2 }}>
            {FILTROS.map(f => (
              <button key={f.value} onClick={() => handleFiltroEstado(f.value)} style={{
                border: 'none', borderRadius: 20, padding: '3px 12px', fontSize: 12, fontWeight: 600,
                cursor: 'pointer', whiteSpace: 'nowrap',
                background: filtroEstado === f.value ? 'var(--sn-accion)' : 'var(--sn-superficie-3)', // Azul si activo, gris si no
                color: filtroEstado === f.value ? 'var(--sn-accion-texto)' : 'var(--sn-texto-2)',
              }}>{f.label}</button>
            ))}
          </div>
        </div>

        {/* Pantalla de carga */}
        {loading && (
          <div className="d-flex flex-column justify-content-center align-items-center" style={{ minHeight: '80vh', textAlign: 'center' }}>
            <style>{`@keyframes parpadeo { 0%,100%{opacity:1} 50%{opacity:0.2} }`}</style>
            <img
              src="/sisnetelclaro.png"
              alt="Sisnetel"
              style={{ width: 160, marginBottom: 24, animation: 'parpadeo 1.4s ease-in-out infinite' }}
            />
            <p className="text-secondary" style={{ fontSize: 14 }}>Sincronizando clientes...</p>
          </div>
        )}

        {/* Mensaje de error */}
        {error && (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--sn-peligro)' }}>
            <div style={{ fontSize: 36 }}>⚠️</div>
            <p className="mt-2" style={{ fontSize: 13 }}>{error}</p>
          </div>
        )}

        {/* Lista de clientes */}
        {!loading && !error && (
          <>
            {/* Selector de cantidad por página */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px 4px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: 12, color: 'var(--sn-texto-3)' }}>Mostrar:</span>
              {PAGE_SIZES.map(s => (
                <button key={s} onClick={() => handlePageSize(s)} style={{
                  border: 'none', borderRadius: 6, padding: '3px 10px', fontSize: 12, fontWeight: 600,
                  cursor: 'pointer',
                  background: pageSize === s ? 'var(--sn-accion)' : 'var(--sn-superficie-3)',
                  color: pageSize === s ? 'var(--sn-accion-texto)' : 'var(--sn-texto-2)',
                }}>{s === 0 ? 'Todos' : s}</button>
              ))}
              {/* Indicador de página actual */}
              {pageSize !== 0 && (
                <span style={{ fontSize: 12, color: 'var(--sn-texto-3)', marginLeft: 'auto' }}>
                  Pág. {paginaActual} / {totalPaginas}
                </span>
              )}
            </div>

            {/* Tarjetas de clientes */}
            <div style={{ padding: '4px 12px 8px' }}>
              {paginados.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 40, color: 'var(--sn-texto-3)' }}>
                  <div style={{ fontSize: 36 }}>🔍</div>
                  <p className="mt-2" style={{ fontSize: 13 }}>No se encontraron clientes.</p>
                </div>
              ) : paginados.map(c => {
                const av = avatarBg(c.estado);
                return (
                  // Al tocar la tarjeta, guardamos el cliente en "detalle" para mostrar su pantalla completa
                  <div key={c.id} onClick={() => setDetalle(c)} style={{
                    background: 'var(--sn-superficie)', borderRadius: 10,
                    boxShadow: 'var(--sn-sombra)',
                    padding: '10px 12px', marginBottom: 6, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: 10,
                    borderLeft: `4px solid ${borderColor(c.estado)}`, // Borde de color según estado
                  }}>
                    {/* Avatar circular con la inicial del nombre */}
                    <div style={{
                      width: 38, height: 38, borderRadius: '50%',
                      background: av.bg, color: av.color,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0, fontWeight: 700, fontSize: 15,
                    }}>
                      {c.nombre?.charAt(0).toUpperCase()}
                    </div>

                    {/* Información del cliente */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {c.nombre}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--sn-texto-3)', marginTop: 1 }}>
                        {/* ID en formato monospace para distinguirlo visualmente */}
                        <span style={{ background: 'var(--sn-superficie-2)', borderRadius: 4, padding: '1px 5px', marginRight: 4, fontFamily: 'monospace' }}>
                          #{c.id}
                        </span>
                        {c.cedula} · {c.movil || '—'}
                      </div>
                      <div style={{ fontSize: 10, color: 'var(--sn-texto-3)', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {c.direccion_principal || 'Sin dirección'}
                      </div>
                    </div>

                    {/* Badge de estado y cantidad de servicios */}
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      {estadoBadge(c.estado)}
                      <div style={{ fontSize: 10, color: 'var(--sn-texto-3)', marginTop: 3 }}>{c.servicios} serv.</div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* ── Paginador ── */}
            {pageSize !== 0 && totalPaginas > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 6, padding: '8px 14px 16px', flexWrap: 'wrap' }}>
                {/* Primera página */}
                <button onClick={() => setPagina(1)} disabled={paginaActual === 1} style={btnPage(paginaActual === 1)}>«</button>
                {/* Página anterior */}
                <button onClick={() => setPagina(p => Math.max(1, p - 1))} disabled={paginaActual === 1} style={btnPage(paginaActual === 1)}>‹</button>

                {/* Botones de páginas cercanas a la actual (máximo 5) */}
                {Array.from({ length: Math.min(5, totalPaginas) }, (_, i) => {
                  let p = paginaActual - 2 + i; // Centramos en la página actual
                  if (p < 1) p = i + 1;
                  if (p > totalPaginas) p = totalPaginas - (4 - i);
                  if (p < 1 || p > totalPaginas) return null;
                  return (
                    <button key={p} onClick={() => setPagina(p)} style={{
                      ...btnPage(false),
                      background: p === paginaActual ? 'var(--sn-accion)' : 'var(--sn-superficie-2)', // Azul la página actual
                      color: p === paginaActual ? 'var(--sn-accion-texto)' : 'var(--sn-texto-2)',
                      fontWeight: p === paginaActual ? 700 : 400,
                    }}>{p}</button>
                  );
                })}

                {/* Página siguiente */}
                <button onClick={() => setPagina(p => Math.min(totalPaginas, p + 1))} disabled={paginaActual === totalPaginas} style={btnPage(paginaActual === totalPaginas)}>›</button>
                {/* Última página */}
                <button onClick={() => setPagina(totalPaginas)} disabled={paginaActual === totalPaginas} style={btnPage(paginaActual === totalPaginas)}>»</button>
              </div>
            )}
          </>
        )}
      </div>
      <BottomNav />
    </div>
  );
};

/** Estilos para los botones del paginador
 *  disabled=true → color gris y cursor normal (no clickeable)
 *  disabled=false → color oscuro y cursor pointer */
const btnPage = (disabled: boolean): React.CSSProperties => ({
  border: 'none', borderRadius: 6, padding: '5px 10px', fontSize: 13,
  cursor: disabled ? 'default' : 'pointer',
  background: 'var(--sn-superficie-2)', color: disabled ? 'var(--sn-borde-fuerte)' : 'var(--sn-texto-2)',
  minWidth: 32,
});

export default Clientes;
